/**
 * Phase 7C — Budget Cadence bulk-edit screen (EAF only).
 *
 * Lists every active BU + Department in one table with their current
 * `recurrence_months` as an inline dropdown. Dirty rows get highlighted; a
 * single "Save Changes" button POSTs the diff to /api/v3/org/budget-cadence/
 * bulk-recurrence. Lets the EAF rapidly configure cadences at FY-start.
 */

import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, fetchPost } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import {
  ALLOWED_RECURRENCE_MONTHS,
  RECURRENCE_LABELS,
} from "../models/business_unit_model";

interface Row {
  id: string;
  name: string;
  entity_type: "bu" | "dept";
  recurrence_months: number; // current persisted value
  draft: number; // editable value in this session
}

const BudgetCadenceScreen: FunctionComponent = () => {
  const navigation = useHistory();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);

  const url_base = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/org`;

  const load = async () => {
    setLoading(true);
    try {
      const [buRes, dpRes] = await Promise.all([
        fetchGet(`${url_base}/business-unit?all=true`),
        fetchGet(`${url_base}/department?all=true`),
      ]);
      const bus = (buRes?.data?.data ?? []) as any[];
      const depts = (dpRes?.data?.data ?? []) as any[];
      const mk = (e: any, t: "bu" | "dept"): Row => ({
        id: e.id,
        name: e.name,
        entity_type: t,
        recurrence_months: Number(e.recurrence_months) || 1,
        draft: Number(e.recurrence_months) || 1,
      });
      setRows([
        ...bus.map((b) => mk(b, "bu")),
        ...depts.map((d) => mk(d, "dept")),
      ]);
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const dirtyRows = useMemo(
    () => rows.filter((r) => r.draft !== r.recurrence_months),
    [rows],
  );

  const save = async () => {
    if (dirtyRows.length === 0) {
      showToast({ type: ToastType.info, text: "No changes to save." });
      return;
    }
    setLoading(true);
    try {
      const res = await fetchPost(`${url_base}/budget-cadence/bulk-recurrence`, {
        rows: dirtyRows.map((r) => ({
          id: r.id,
          entity_type: r.entity_type,
          recurrence_months: r.draft,
        })),
      });
      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        await load();
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e.message });
    }
    setLoading(false);
  };

  const setDraft = (id: string, value: number) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, draft: value } : r)),
    );
  };

  return (
    <div className="p-3 sm:p-5 flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            Budget Cadence
          </h1>
          <p className="text-xs text-gray-500">
            Set how often each Business Unit / Department recalibrates its
            annual estimate against actuals. Defaults to monthly.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <button
            onClick={() => navigation.goBack()}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={dirtyRows.length === 0 || loading}
            className="bg-myPrimaryColor text-white rounded-md px-4 py-1.5 text-sm disabled:opacity-40"
          >
            Save Changes
            {dirtyRows.length > 0 && (
              <span className="ml-2 bg-white/20 rounded-full px-2 text-xs">
                {dirtyRows.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Type</th>
              <th className="text-left px-3 py-2 font-semibold">ID</th>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Cadence</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-500">
                  No Business Units or Departments configured yet.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const dirty = r.draft !== r.recurrence_months;
              return (
                <tr
                  key={r.entity_type + ":" + r.id}
                  className={`border-t ${dirty ? "bg-amber-50/60" : "hover:bg-gray-50"}`}
                >
                  <td className="px-3 py-2">
                    <span
                      className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                        r.entity_type === "bu"
                          ? "bg-sky-50 text-sky-800"
                          : "bg-purple-50 text-purple-800"
                      }`}
                    >
                      {r.entity_type === "bu" ? "BU" : "Dept"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">
                    {r.id}
                  </td>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`cadence-${r.id}`}
                      value={r.draft}
                      onChange={(e) =>
                        setDraft(r.id, Number(e.target.value))
                      }
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                    >
                      {ALLOWED_RECURRENCE_MONTHS.map((n) => (
                        <option key={n} value={n}>
                          {RECURRENCE_LABELS[n]} ({n}mo)
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {dirty ? (
                      <span className="text-amber-700 font-semibold">
                        Modified ({r.recurrence_months}mo → {r.draft}mo)
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};

export default BudgetCadenceScreen;
